import React, { useState, useEffect, useCallback } from 'react';
import { apiService } from '../../services/api';

interface Badge {
  id: string;
  badge_type: string;
  badge_name: string;
  badge_description: string;
  badge_icon: string;
  earned_at: string;
  context?: string;
}

interface Streak {
  streak_type: string;
  current_streak: number;
  longest_streak: number;
  last_activity_date: string;
}

interface LeaderboardEntry {
  user_id: string;
  username: string;
  first_name: string;
  last_name: string;
  points: number;
  tasks_completed: number;
  rank: number;
}

interface Notification {
  id: string;
  notification_type: string;
  title: string;
  message: string;
  created_at: string;
}

const Gamification: React.FC = () => {
  const [badges, setBadges] = useState<Badge[]>([]);
  const [streaks, setStreaks] = useState<Record<string, Streak>>({});
  const [weeklyStats, setWeeklyStats] = useState({ points: 0, tasks_completed: 0, habits_completed: 0 });
  const [totalPoints, setTotalPoints] = useState(0);
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [userRank, setUserRank] = useState<number | null>(null);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [allBadges, setAllBadges] = useState<Record<string, any>>({});
  const [activityMap, setActivityMap] = useState<Record<string, number>>({});
  const [topWins, setTopWins] = useState<string[]>([]);
  const [habitsThisWeek, setHabitsThisWeek] = useState(0);
  const [weekStart, setWeekStart] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'recap' | 'badges' | 'leaderboard' | 'streaks'>('recap');

  const fetchStats = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiService.getGamificationStats();
      if (response.success) {
        setBadges(response.badges || []);
        setStreaks(response.streaks || {});
        setWeeklyStats(response.weeklyStats || {});
        setTotalPoints(response.totalPoints || 0);
        setNotifications(response.notifications || []);
        setAllBadges(response.allBadges || {});
        setActivityMap(response.activityMap || {});
        setTopWins(response.topWins || []);
        setHabitsThisWeek(response.habitsThisWeek || 0);
        setWeekStart(response.weekStart || '');
      }
    } catch (error) {
      console.error('Failed to fetch gamification stats:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchLeaderboard = useCallback(async () => {
    try {
      const response = await apiService.getLeaderboard('week');
      if (response.success) {
        setLeaderboard(response.leaderboard || []);
        setUserRank(response.userRank);
      }
    } catch (error) {
      console.error('Failed to fetch leaderboard:', error);
    }
  }, []);

  useEffect(() => {
    fetchStats();
    fetchLeaderboard();
  }, [fetchStats, fetchLeaderboard]);

  const markNotificationsRead = async () => {
    if (notifications.length === 0) return;
    try {
      await apiService.markNotificationsRead(notifications.map(n => n.id));
      setNotifications([]);
    } catch (error) {
      console.error('Failed to mark notifications read:', error);
    }
  };

  const earnedBadgeTypes = new Set(badges.map(b => b.badge_type));
  const earnedByType = new Map(badges.map(b => [b.badge_type, b]));

  const formatEarnedDate = (iso?: string) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const renderHeatmap = () => {
    const pad = (n: number) => n.toString().padStart(2, '0');
    const keyOf = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const start = new Date(today);
    start.setDate(start.getDate() - start.getDay() - 12 * 7);
    const weeks: Date[][] = [];
    const cur = new Date(start);
    while (cur <= today) {
      const week: Date[] = [];
      for (let i = 0; i < 7; i++) { week.push(new Date(cur)); cur.setDate(cur.getDate() + 1); }
      weeks.push(week);
    }
    const COLORS = ['#f1f5f9', '#fde68a', '#fdba74', '#f97316', '#c2410c'];
    const levelOf = (c: number) => (c === 0 ? 0 : c === 1 ? 1 : c <= 3 ? 2 : c <= 5 ? 3 : 4);
    const todayKey = keyOf(today);
    const inRange = (k: string) => k >= keyOf(start) && k <= todayKey;
    const activeDays = Object.keys(activityMap).filter(k => inRange(k) && (activityMap[k] || 0) > 0).length;
    const best = Object.entries(activityMap).filter(([k]) => inRange(k)).sort((a, b) => b[1] - a[1])[0];
    const CELL = 15;
    return (
      <div data-testid="streak-heatmap" style={{ padding: '20px', backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e5e7eb', marginBottom: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: '8px', marginBottom: '14px' }}>
          <h4 style={{ margin: 0, fontSize: '0.95rem', color: '#374151' }}>🔥 Activity Heatmap <span style={{ fontWeight: 400, color: '#9ca3af', fontSize: '0.75rem' }}>last 13 weeks</span></h4>
          <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>
            <strong style={{ color: '#ea580c' }}>{activeDays}</strong> active days
            {best && best[1] > 0 && <> · best day: <strong style={{ color: '#ea580c' }}>{best[1]} tasks</strong> ({formatEarnedDate(best[0] + 'T12:00:00')})</>}
          </span>
        </div>
        <div style={{ display: 'flex', gap: '4px', overflowX: 'auto', paddingBottom: '4px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '18px', flexShrink: 0 }}>
            {['', 'Mon', '', 'Wed', '', 'Fri', ''].map((d, i) => (
              <div key={i} style={{ height: `${CELL}px`, fontSize: '0.6rem', color: '#9ca3af', lineHeight: `${CELL}px`, width: '26px' }}>{d}</div>
            ))}
          </div>
          {weeks.map((week, wi) => (
            <div key={wi} style={{ display: 'flex', flexDirection: 'column', gap: '4px', flexShrink: 0 }}>
              <div style={{ height: '14px', fontSize: '0.6rem', color: '#6b7280', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'visible' }}>
                {(wi === 0 || week[0].getMonth() !== weeks[wi - 1][0].getMonth()) ? week[0].toLocaleDateString(undefined, { month: 'short' }) : ''}
              </div>
              {week.map((day, di) => {
                if (day > today) return <div key={di} style={{ width: `${CELL}px`, height: `${CELL}px` }} />;
                const k = keyOf(day);
                const c = activityMap[k] || 0;
                return (
                  <div
                    key={di}
                    data-testid={`heat-cell-${k}`}
                    title={`${day.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })} — ${c} task${c === 1 ? '' : 's'} completed`}
                    style={{
                      width: `${CELL}px`, height: `${CELL}px`, borderRadius: '3px',
                      backgroundColor: COLORS[levelOf(c)],
                      border: k === todayKey ? '1.5px solid #ef4444' : '1px solid rgba(0,0,0,0.04)',
                      boxSizing: 'border-box', cursor: 'default'
                    }}
                  />
                );
              })}
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '10px', fontSize: '0.65rem', color: '#6b7280', justifyContent: 'flex-end' }}>
          <span>Less</span>
          {['#f1f5f9', '#fde68a', '#fdba74', '#f97316', '#c2410c'].map(c => (
            <div key={c} style={{ width: '12px', height: '12px', borderRadius: '3px', backgroundColor: c }} />
          ))}
          <span>More</span>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <p>Loading achievements...</p>
      </div>
    );
  }

  const shareRecap = async () => {
    const rangeLabel = weekStart ? `week of ${formatEarnedDate(weekStart + 'T12:00:00')}` : 'this week';
    const lines = [
      `📅 My ${rangeLabel} on Yojana`,
      ``,
      `✅ ${weeklyStats.tasks_completed} tasks completed`,
      `🎯 ${habitsThisWeek} habit check-ins`,
      `🔥 ${streaks.daily?.current_streak || 0}-day streak (best ${streaks.daily?.longest_streak || 0})`,
    ];
    if (topWins.length) {
      lines.push(``, `Top wins:`);
      topWins.forEach(w => lines.push(`• ${w}`));
    }
    const text = lines.join('\n');
    try {
      if (typeof navigator !== 'undefined' && (navigator as any).share) {
        await (navigator as any).share({ title: 'My Yojana week', text });
      } else if (navigator.clipboard) {
        await navigator.clipboard.writeText(text);
        alert('Recap copied to clipboard — paste it anywhere to share!');
      }
    } catch (e) { /* user cancelled share */ }
  };

  const renderRecap = () => {
    const rangeLabel = weekStart ? formatEarnedDate(weekStart + 'T12:00:00') : 'This week';
    const dailyStreak = streaks.daily?.current_streak || 0;
    const bestStreak = streaks.daily?.longest_streak || 0;
    const recapCards = [
      { label: 'Tasks Completed', value: weeklyStats.tasks_completed, color: '#a21caf', bg: '#fdf4ff' },
      { label: 'Habit Check-ins', value: habitsThisWeek, color: '#0369a1', bg: '#f0f9ff' },
      { label: 'Current Streak', value: dailyStreak, color: '#c2410c', bg: '#fff7ed' },
      { label: 'Best Streak', value: bestStreak, color: '#15803d', bg: '#f0fdf4' }
    ];
    return (
      <div data-testid="weekly-recap">
        {/* Hero */}
        <div style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', borderRadius: '16px', padding: '24px', color: 'white', marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <p style={{ margin: 0, fontSize: '0.8rem', opacity: 0.85, letterSpacing: '0.05em', textTransform: 'uppercase' }}>Weekly Recap</p>
            <h3 style={{ margin: '4px 0 0 0', fontSize: '1.4rem', fontWeight: 800 }}>Week of {rangeLabel}</h3>
            <p style={{ margin: '6px 0 0 0', fontSize: '0.9rem', opacity: 0.9 }}>
              {weeklyStats.tasks_completed} tasks · {habitsThisWeek} habit check-ins · {dailyStreak}-day streak 🔥
            </p>
          </div>
          <button
            onClick={shareRecap}
            data-testid="share-recap-btn"
            style={{ padding: '12px 22px', backgroundColor: 'rgba(255,255,255,0.2)', color: 'white', border: '2px solid rgba(255,255,255,0.5)', borderRadius: '12px', fontWeight: 700, cursor: 'pointer', fontSize: '0.9rem', whiteSpace: 'nowrap' }}
          >
            Share my week ↗
          </button>
        </div>

        {/* Stat cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '14px', marginBottom: '20px' }}>
          {recapCards.map(c => (
            <div key={c.label} style={{ padding: '18px', backgroundColor: c.bg, borderRadius: '12px', textAlign: 'center' }}>
              <p style={{ margin: 0, fontSize: '2rem', fontWeight: 800, color: c.color }}>{c.value}</p>
              <p style={{ margin: '2px 0 0 0', fontSize: '0.8rem', color: c.color }}>{c.label}</p>
            </div>
          ))}
        </div>

        {/* Heatmap */}
        {renderHeatmap()}

        {/* Top wins */}
        <div style={{ padding: '20px', backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e5e7eb' }}>
          <h4 style={{ margin: '0 0 12px 0', fontSize: '0.95rem', color: '#374151' }}>🏅 Top Wins This Week</h4>
          {topWins.length === 0 ? (
            <p style={{ margin: 0, color: '#9ca3af', fontSize: '0.85rem' }}>No completed tasks yet this week — knock a few out and they'll show up here!</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {topWins.map((w, i) => (
                <div key={i} data-testid={`top-win-${i}`} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', backgroundColor: '#f9fafb', borderRadius: '8px' }}>
                  <span style={{ fontSize: '1.1rem' }}>✅</span>
                  <span style={{ fontSize: '0.9rem', color: '#1f2937', fontWeight: 500 }}>{w}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div style={{ padding: '24px' }} data-testid="gamification">
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700, color: '#1f2937' }}>Achievements & Progress</h2>
        <p style={{ margin: '4px 0 0 0', color: '#6b7280', fontSize: '0.9rem' }}>Track your productivity journey</p>
      </div>

      {/* Notifications */}
      {notifications.length > 0 && (
        <div style={{ marginBottom: '24px', padding: '16px', backgroundColor: '#fef3c7', border: '1px solid #fbbf24', borderRadius: '12px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <h4 style={{ margin: 0, color: '#92400e' }}>New Achievements!</h4>
            <button onClick={markNotificationsRead} style={{ background: 'none', border: 'none', color: '#92400e', cursor: 'pointer', fontSize: '0.85rem' }}>Dismiss all</button>
          </div>
          {notifications.map(n => (
            <div key={n.id} style={{ padding: '8px 0', borderTop: '1px solid #fde68a' }}>
              <p style={{ margin: 0, fontWeight: 600, color: '#78350f' }}>{n.title}</p>
              <p style={{ margin: '2px 0 0 0', fontSize: '0.85rem', color: '#92400e' }}>{n.message}</p>
            </div>
          ))}
        </div>
      )}

      {/* Stats Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '16px', marginBottom: '24px' }}>
        <div style={{ padding: '20px', backgroundColor: '#f0fdf4', borderRadius: '12px', textAlign: 'center', border: '1px solid #bbf7d0' }}>
          <p style={{ margin: 0, fontSize: '2rem', fontWeight: 700, color: '#15803d' }}>{totalPoints}</p>
          <p style={{ margin: '4px 0 0 0', fontSize: '0.85rem', color: '#166534' }}>Total Points</p>
        </div>
        <div style={{ padding: '20px', backgroundColor: '#eff6ff', borderRadius: '12px', textAlign: 'center', border: '1px solid #bfdbfe' }}>
          <p style={{ margin: 0, fontSize: '2rem', fontWeight: 700, color: '#1d4ed8' }}>{badges.length}</p>
          <p style={{ margin: '4px 0 0 0', fontSize: '0.85rem', color: '#1e40af' }}>Badges Earned</p>
        </div>
        <div style={{ padding: '20px', backgroundColor: '#fdf4ff', borderRadius: '12px', textAlign: 'center', border: '1px solid #f5d0fe' }}>
          <p style={{ margin: 0, fontSize: '2rem', fontWeight: 700, color: '#a21caf' }}>{weeklyStats.tasks_completed}</p>
          <p style={{ margin: '4px 0 0 0', fontSize: '0.85rem', color: '#86198f' }}>Tasks This Week</p>
        </div>
        <div style={{ padding: '20px', backgroundColor: '#fff7ed', borderRadius: '12px', textAlign: 'center', border: '1px solid #fed7aa' }}>
          <p style={{ margin: 0, fontSize: '2rem', fontWeight: 700, color: '#c2410c' }}>{streaks.daily?.current_streak || 0}</p>
          <p style={{ margin: '4px 0 0 0', fontSize: '0.85rem', color: '#9a3412' }}>Day Streak</p>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', borderBottom: '2px solid #e5e7eb', paddingBottom: '8px' }}>
        {(['recap', 'badges', 'leaderboard', 'streaks'] as const).map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            data-testid={`tab-${tab}`}
            style={{
              padding: '10px 20px',
              backgroundColor: activeTab === tab ? '#667eea' : 'transparent',
              color: activeTab === tab ? 'white' : '#6b7280',
              border: 'none',
              borderRadius: '8px',
              fontWeight: 600,
              cursor: 'pointer',
              textTransform: 'capitalize'
            }}
          >
            {tab === 'recap' ? 'Weekly Recap' : tab}
          </button>
        ))}
      </div>

      {/* Weekly Recap Tab */}
      {activeTab === 'recap' && renderRecap()}

      {/* Badges Tab */}
      {activeTab === 'badges' && (
        <div>
          <h3 style={{ margin: '0 0 16px 0', fontSize: '1.1rem', color: '#374151' }}>Your Badges</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '16px' }}>
            {Object.entries(allBadges).map(([type, badge]: [string, any]) => {
              const earned = earnedBadgeTypes.has(type);
              const earnedInfo = earnedByType.get(type);
              return (
                <div
                  key={type}
                  data-testid={`badge-${type}`}
                  style={{
                    padding: '20px',
                    backgroundColor: earned ? '#f0fdf4' : '#f9fafb',
                    borderRadius: '12px',
                    textAlign: 'center',
                    border: earned ? '2px solid #22c55e' : '1px solid #e5e7eb',
                    opacity: earned ? 1 : 0.6
                  }}
                >
                  <span style={{ fontSize: '2.5rem', filter: earned ? 'none' : 'grayscale(1)' }}>{badge.icon}</span>
                  <p style={{ margin: '8px 0 4px 0', fontWeight: 600, fontSize: '0.9rem', color: earned ? '#15803d' : '#6b7280' }}>
                    {badge.name}
                  </p>
                  <p style={{ margin: 0, fontSize: '0.75rem', color: '#9ca3af' }}>{badge.description}</p>
                  {earned ? (
                    <div style={{ marginTop: '10px', paddingTop: '8px', borderTop: '1px dashed #bbf7d0' }}>
                      {earnedInfo?.context && (
                        <p style={{ margin: '0 0 2px 0', fontSize: '0.72rem', color: '#166534', fontWeight: 600 }} data-testid={`badge-context-${type}`}>
                          {earnedInfo.context}
                        </p>
                      )}
                      <p style={{ margin: 0, fontSize: '0.7rem', color: '#22c55e', fontWeight: 700 }} data-testid={`badge-earned-date-${type}`}>
                        {formatEarnedDate(earnedInfo?.earned_at) ? `Earned ${formatEarnedDate(earnedInfo?.earned_at)}` : 'EARNED!'}
                      </p>
                    </div>
                  ) : (
                    <p style={{ margin: '10px 0 0 0', fontSize: '0.7rem', color: '#cbd5e1', fontWeight: 600 }}>LOCKED</p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Leaderboard Tab */}
      {activeTab === 'leaderboard' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h3 style={{ margin: 0, fontSize: '1.1rem', color: '#374151' }}>Weekly Leaderboard</h3>
            {userRank && <span style={{ color: '#667eea', fontWeight: 600 }}>Your Rank: #{userRank}</span>}
          </div>
          {leaderboard.length === 0 ? (
            <p style={{ textAlign: 'center', color: '#6b7280', padding: '40px' }}>No leaderboard data yet. Complete tasks to earn points!</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {leaderboard.map((entry, index) => (
                <div
                  key={entry.user_id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '16px',
                    backgroundColor: index < 3 ? ['#fef3c7', '#f3f4f6', '#fed7aa'][index] : 'white',
                    borderRadius: '12px',
                    border: '1px solid #e5e7eb'
                  }}
                >
                  <span style={{ 
                    fontSize: '1.5rem', 
                    fontWeight: 700, 
                    width: '40px',
                    color: index < 3 ? ['#f59e0b', '#6b7280', '#ea580c'][index] : '#374151'
                  }}>
                    {index < 3 ? ['🥇', '🥈', '🥉'][index] : `#${index + 1}`}
                  </span>
                  <div style={{ flex: 1, marginLeft: '12px' }}>
                    <p style={{ margin: 0, fontWeight: 600, color: '#1f2937' }}>
                      {entry.first_name || entry.username}
                    </p>
                    <p style={{ margin: 0, fontSize: '0.8rem', color: '#6b7280' }}>
                      {entry.tasks_completed} tasks completed
                    </p>
                  </div>
                  <span style={{ fontWeight: 700, fontSize: '1.25rem', color: '#667eea' }}>{entry.points} pts</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Streaks Tab */}
      {activeTab === 'streaks' && (
        <div>
          {renderHeatmap()}
          <h3 style={{ margin: '0 0 16px 0', fontSize: '1.1rem', color: '#374151' }}>Your Streaks</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '16px' }}>
            {[
              { type: 'daily', name: 'Daily Activity', icon: '🔥' },
              { type: 'tasks', name: 'Task Completion', icon: '✅' },
              { type: 'habits', name: 'Habit Streak', icon: '🎯' }
            ].map(({ type, name, icon }) => {
              const streak = streaks[type];
              return (
                <div key={type} style={{ padding: '20px', backgroundColor: '#f9fafb', borderRadius: '12px', border: '1px solid #e5e7eb' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                    <span style={{ fontSize: '1.5rem' }}>{icon}</span>
                    <span style={{ fontWeight: 600, color: '#374151' }}>{name}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <div>
                      <p style={{ margin: 0, fontSize: '2rem', fontWeight: 700, color: '#667eea' }}>{streak?.current_streak || 0}</p>
                      <p style={{ margin: 0, fontSize: '0.75rem', color: '#6b7280' }}>Current</p>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <p style={{ margin: 0, fontSize: '1.25rem', fontWeight: 600, color: '#9ca3af' }}>{streak?.longest_streak || 0}</p>
                      <p style={{ margin: 0, fontSize: '0.75rem', color: '#6b7280' }}>Best</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default Gamification;
